import { useMemo, type ReactNode } from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { Badge, Button, Card, Typography, type BadgeVariant } from '../ui';
import { resolveInAppPath } from '../../../lib/app-link';
import {
  customerHistoryView,
  customerLookupKeys,
  formatCustomerDate,
  formatCustomerDateTime,
  type BookingRowView,
  type ChipTone,
  type CustomerChip,
  type CustomerView,
  type SectionView,
} from '../../../lib/customer-history';
import { useCustomerHistory } from '../../../lib/use-customer-history';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTranslation } from '../../../lib/i18n';
import styles from './contacts.module.css';

const TONE_VARIANT: Record<ChipTone, BadgeVariant> = {
  positive: 'done',
  attention: 'waiting-on-you',
  danger: 'failed',
  neutral: 'waiting-on-a-reply',
};

type ButtonName = 'customer_order' | 'customer_booking' | 'customer_invoice' | 'customer_inbox_reply';

/**
 * The contact's history as a customer of the owner's Wix sites — orders, bookings, invoices and the Wix Inbox,
 * a section per site, placed right after Channels. Contract: `native-apps/shared/customer-history/contract.md`;
 * every rule is in `lib/customer-history.ts`. Shared 1:1 with iOS `ContactDetailScreen` and Android
 * `ContactDetail.kt`: the same states, order, copy and links. Draws nothing while loading (no spinner flash),
 * for a contact with no email or phone, when the route is not deployed (404), or when the owner's
 * `symphonyCustomerHistory` toggle is off.
 */
export function CustomerHistorySection({
  channels,
  contactName = '',
}: {
  channels: ReadonlyArray<{ kind?: string; value?: string }> | undefined;
  contactName?: string;
}) {
  const { t, i18n } = useTranslation();
  const keys = useMemo(() => customerLookupKeys(channels), [channels]);
  const { data } = useCustomerHistory(keys);
  const view = useMemo(() => customerHistoryView(data, (key, params) => t(key, params), undefined, contactName), [data, t, contactName]);
  const locale = i18n.language || 'en';

  if (view.kind === 'nothing') return null;
  if (view.kind === 'sitesUnreadable') {
    return (
      <div className={styles.detailSection}>
        <Typography variant="h3" color="primary">
          {t('contacts.customer.titleNoSite')}
        </Typography>
        <Card variant="sunken" size="lg">
          <Typography variant="p2" color="secondary">
            {t('contacts.customer.sitesUnreadable')}
          </Typography>
        </Card>
      </div>
    );
  }
  return (
    <>
      {view.customers.map((customer) => (
        <CustomerSection key={customer.key} customer={customer} locale={locale} />
      ))}
    </>
  );
}

function CustomerSection({ customer, locale }: { customer: CustomerView; locale: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const click = (button_name: ButtonName) => () => logPageClickBi({ page_name: 'contact_detail', button_name });

  return (
    <div className={styles.detailSection}>
      <Typography variant="h3" color="primary">
        {t(customer.title.key, customer.title.params)}
      </Typography>
      <Card variant="sunken" size="lg" className={styles.customerCard}>
        {customer.summary.length ? (
          <div className={styles.customerSummary}>
            {customer.summary.map((row) => (
              <div key={row.labelKey} className={styles.customerSummaryRow}>
                <Typography variant="p2" color="secondary">
                  {t(row.labelKey)}
                </Typography>
                <Typography variant="h4" color="primary">
                  {row.value.kind === 'money'
                    ? row.value.formatted
                    : row.value.kind === 'date'
                      ? formatCustomerDate(row.value.at, locale)
                      : t('contacts.customer.unreadable')}
                </Typography>
              </div>
            ))}
          </div>
        ) : null}

        <SubSection title={t('contacts.customer.orders.title')} section={customer.orders}>
          {(orders) => (
            <>
              <div className={styles.needsRows}>
                {orders.rows.map((order) => (
                  <ItemRow
                    key={order.id}
                    href={order.link}
                    onClick={click('customer_order')}
                    title={t('contacts.customer.orders.number', { number: order.number })}
                    line={[formatCustomerDate(order.at, locale), order.total]}
                    chips={order.chips}
                  />
                ))}
              </div>
              {orders.totalCount !== null ? (
                <Typography variant="p3" color="secondary">
                  {t('contacts.customer.orders.count', { count: orders.totalCount })}
                </Typography>
              ) : null}
            </>
          )}
        </SubSection>

        <SubSection title={t('contacts.customer.bookings.title')} section={customer.bookings}>
          {(bookings) => (
            <>
              <BookingGroup label={t('contacts.customer.bookings.upcoming')} rows={bookings.upcoming} locale={locale} onClick={click('customer_booking')} />
              <BookingGroup label={t('contacts.customer.bookings.past')} rows={bookings.past} locale={locale} onClick={click('customer_booking')} />
            </>
          )}
        </SubSection>

        <SubSection title={t('contacts.customer.invoices.title')} section={customer.invoices}>
          {(invoices) => (
            <div className={styles.needsRows}>
              {invoices.rows.map((invoice) => (
                <ItemRow
                  key={invoice.id}
                  href={invoice.link}
                  onClick={click('customer_invoice')}
                  title={t('contacts.customer.invoices.number', { number: invoice.number })}
                  line={[formatCustomerDate(invoice.at, locale), invoice.total]}
                  chips={invoice.chip ? [invoice.chip] : []}
                />
              ))}
            </div>
          )}
        </SubSection>

        <SubSection title={t('contacts.customer.inbox.title')} section={customer.inbox}>
          {(inbox) => (
            <>
              <div className={styles.customerMessages}>
                {inbox.messages.map((message, i) => (
                  <div key={`${message.at ?? ''}:${i}`} className={message.fromCustomer ? styles.customerMessageTheirs : styles.customerMessageMine}>
                    <Card variant="stone" size="sm" className={styles.customerMessageBubble}>
                      <Typography variant="p3" color="secondary">
                        {message.speaker}
                      </Typography>
                      {/* The customer's own words: plain text, never markup or a link, in their own direction. */}
                      <Typography variant="p2" color="primary" className={styles.customerMessageText} dir={message.dir}>
                        {message.text}
                      </Typography>
                    </Card>
                  </div>
                ))}
              </div>
              <div className={styles.customerInboxFoot}>
                <Typography variant="p3" color="secondary">
                  {formatCustomerDateTime(inbox.lastMessageAt, locale)}
                </Typography>
                <InAppButton href={inbox.replyLink} onClick={click('customer_inbox_reply')}>
                  {t('contacts.customer.inbox.reply')}
                </InAppButton>
              </div>
            </>
          )}
        </SubSection>
      </Card>
    </div>
  );
}

/** A sub-section: its title, then `unreadable` for a failed read, `none` for an empty one, or its rows. */
function SubSection<T>({ title, section, children }: { title: string; section: SectionView<T>; children: (items: T) => ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className={styles.customerSubSection}>
      <Typography variant="h4" color="primary">
        {title}
      </Typography>
      {section.kind === 'items' ? (
        children(section as T)
      ) : (
        <Typography variant="p2" color="secondary">
          {t(section.kind === 'unreadable' ? 'contacts.customer.unreadable' : 'contacts.customer.none')}
        </Typography>
      )}
    </div>
  );
}

function BookingGroup({ label, rows, locale, onClick }: { label: string; rows: BookingRowView[]; locale: string; onClick: () => void }) {
  if (!rows.length) return null;
  return (
    <div className={styles.customerBookingGroup}>
      <Typography variant="p3" color="secondary">
        {label}
      </Typography>
      <div className={styles.needsRows}>
        {rows.map((booking) => (
          <ItemRow
            key={booking.id}
            href={booking.link}
            onClick={onClick}
            title={booking.serviceName}
            line={[formatCustomerDateTime(booking.start, locale)]}
            chips={booking.chip ? [booking.chip] : []}
          />
        ))}
      </div>
    </div>
  );
}

/** The web route an `orion://` link resolves to, through the app's one resolver (`resolveInAppPath`). */
function useInAppTarget(href: string | null) {
  const basePath = useBasePath();
  const path = href ? resolveInAppPath(href) : null;
  return path ? { path, as: `${basePath}${path}` } : null;
}

function ItemRow({
  href,
  onClick,
  title,
  line,
  chips,
}: {
  href: string | null;
  onClick: () => void;
  title: string;
  line: Array<string | undefined>;
  chips: CustomerChip[];
}) {
  const { t } = useTranslation();
  const target = useInAppTarget(href);
  const body = (
    <>
      <span className={styles.outreachRowMain}>
        <Typography variant="h4" color="primary">
          {title}
        </Typography>
        <Typography variant="p3" color="secondary">
          {line.filter(Boolean).join(' · ')}
        </Typography>
        {chips.length ? (
          <span className={styles.customerChips}>
            {chips.map((chip) => (
              <Badge key={chip.key} variant={TONE_VARIANT[chip.tone]} label={t(chip.key)} />
            ))}
          </span>
        ) : null}
      </span>
      {target ? (
        <span className={styles.needsEnd}>
          <ChevronRight size={24} className="dir-flip" aria-hidden />
        </span>
      ) : null}
    </>
  );
  if (!target) return <div className={styles.needsRow}>{body}</div>;
  return (
    <Link href={target.path} as={target.as} className={styles.needsRow} onClick={onClick}>
      {body}
    </Link>
  );
}

function InAppButton({ href, onClick, children }: { href: string; onClick: () => void; children: ReactNode }) {
  const path = resolveInAppPath(href);
  if (!path) return null;
  return (
    <Button variant="outlined" size="small" href={path} onClick={onClick}>
      {children}
    </Button>
  );
}
